import {
  registerMwa,
  createDefaultAuthorizationCache,
  createDefaultChainSelector,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-standard-mobile';

/**
 * Mobile Wallet Adapter (MWA) Configuration
 *
 * This file configures and registers the Mobile Wallet Adapter for WEB apps.
 * Uses the Wallet Standard protocol (@solana-mobile/wallet-standard-mobile)
 * which is the recommended approach for web applications according to official docs.
 *
 * Features:
 * - Wallet Standard protocol (not wallet-adapter)
 * - Automatic wallet registration
 * - Support for both local (mobile) and remote (desktop QR) connections
 * - Authorization caching for session persistence
 */

/**
 * App identity for MWA
 * This information is shown to users during wallet connection
 */
export const MWA_APP_IDENTITY = {
  name: 'Tweetonium',
  uri: 'https://tweetonium.xyz',
  icon: '/favicon.ico',
};

/**
 * Supported Solana chains
 * MWA supports both mainnet and devnet
 */
export const MWA_CHAINS = ['solana:mainnet', 'solana:devnet'] as const;

/**
 * Initializes and registers the Mobile Wallet Adapter
 *
 * This is the CORRECT way to initialize MWA for web apps according to
 * official Solana Mobile documentation. It registers MWA wallets with
 * the Wallet Standard so they can be discovered and used.
 *
 * Must be called in a non-SSR context (e.g., in main.tsx after DOM is ready).
 *
 * @returns void
 */
export const initializeMWA = (): void => {
  // Only run in browser context (not during SSR)
  if (typeof window === 'undefined') {
    console.warn('[MWA] Skipping initialization in SSR context');
    return;
  }

  const supported = isMWASupported();

  if (!supported) {
    const userAgent = navigator.userAgent;
    const isAndroid = /Android/.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/.test(userAgent);

    if (isIOS) {
      console.log('[MWA] iOS detected - MWA not supported, skipping registration');
    } else if (isAndroid) {
      console.log('[MWA] Android detected but not Chrome - skipping MWA registration');
    } else {
      console.log('[MWA] Desktop detected - skipping MWA registration (Android Chrome only)');
    }
    // Do NOT register MWA on unsupported platforms.
    // registerMwa() creates a Wallet Standard entry that attempts a WebSocket
    // connection (ws://localhost:<port>/solana-wallet) when connect() is called.
    // On non-Android-Chrome platforms this WebSocket will always fail.
    return;
  }

  try {
    // Register MWA with the Wallet Standard
    // This makes MWA wallets discoverable via getWallets() API
    registerMwa({
      // App identity shown to users during connection
      appIdentity: MWA_APP_IDENTITY,

      // Authorization cache for persistent sessions
      // Uses default implementation (localStorage-based)
      authorizationCache: createDefaultAuthorizationCache(),

      // Supported chains (mainnet and devnet)
      chains: MWA_CHAINS,

      // Chain selector (for multi-chain support)
      // Uses default implementation (selects first available chain)
      chainSelector: createDefaultChainSelector(),

      // Handler for when no wallet is found
      // Uses default implementation (shows error message)
      onWalletNotFound: createDefaultWalletNotFoundHandler(),
    });

    console.log('[MWA] Successfully registered Mobile Wallet Adapter with Wallet Standard');
    console.log('[MWA] MWA wallets can now be discovered via getWallets() API');
  } catch (error) {
    console.error('[MWA] Failed to register Mobile Wallet Adapter:', error);
  }
};

/**
 * Checks if MWA is supported on the current platform
 *
 * MWA Intent Protocol Requirements:
 * - Android operating system
 * - Chrome browser (best compatibility)
 * - Not iOS (MWA Intent protocol doesn't work on iOS)
 *
 * Note: Desktop browsers can still use MWA via QR code connection
 *
 * @returns boolean indicating if the platform supports MWA Intent protocol
 */
export const isMWASupported = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent;

  // Check for Android
  const isAndroid = /Android/.test(userAgent);

  // Check for Chrome (and not other browsers)
  const isChrome = /Chrome/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);
  const isOpera = /OPR/.test(userAgent);

  // MWA Intent protocol works best on Android Chrome
  // Firefox, Brave, and Opera may have WebSocket compatibility issues
  // Desktop browsers can still use QR code connection even if this returns false
  return isAndroid && isChrome && !isFirefox && !isOpera;
};

/**
 * Checks if the current device is mobile
 *
 * @returns boolean indicating if device is mobile
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || (window as any).opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  return mobileRegex.test(userAgent);
};
